import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ExternalLink, Loader2, X } from "lucide-react";
import { generateQuiz } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { Textarea } from "~/atoms/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";

interface DocumentToAnchor {
  documentId: number;
  documentReleaseId: number;
  title: string;
}

interface Item extends DocumentToAnchor {
  prompt: string;
}

interface Props {
  documents: DocumentToAnchor[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

const toItems = (documents: DocumentToAnchor[]): Item[] =>
  documents.map((document) => ({ ...document, prompt: "" }));

/**
 * Bulk quiz creation. Lists the selected documents, each with its own optional
 * prompt and remove button, and fires one generate request per quiz. Generation
 * runs in the background, so once the requests are in flight `onCreated` hands
 * off to the quizzes page where each quiz shows up and finishes on its own.
 *
 * Mounted only while open, so the document list seeds fresh state each time.
 */
const CreateQuizzesDialog = ({
  documents,
  open,
  onOpenChange,
  onCreated,
}: Props) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>(() => toItems(documents));
  const [isCreating, setIsCreating] = useState(false);

  const setPrompt = (documentReleaseId: number, prompt: string) =>
    setItems((prev) =>
      prev.map((item) =>
        item.documentReleaseId === documentReleaseId
          ? { ...item, prompt }
          : item,
      ),
    );

  const removeItem = (documentReleaseId: number) =>
    setItems((prev) =>
      prev.filter((item) => item.documentReleaseId !== documentReleaseId),
    );

  const create = async () => {
    setIsCreating(true);
    const results = await Promise.all(
      items.map(async (item) => {
        try {
          await generateQuiz(
            item.documentReleaseId,
            item.prompt.trim() || undefined,
          );
          return true;
        } catch {
          return false;
        }
      }),
    );
    setIsCreating(false);

    const failed = results.filter((ok) => !ok).length;
    if (failed > 0) {
      toast.error(t(($) => $.anchoring.create.error, { count: failed }));
    }

    // As long as something started, hand off to the quizzes page where the
    // generating quizzes appear and finish on their own.
    if (failed < results.length) {
      onCreated();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-200 flex-col">
        <DialogHeader>
          <DialogTitle>{t(($) => $.anchoring.create.title)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.anchoring.create.description)}
          </DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-3 overflow-y-auto">
          {items.map((item) => (
            <li
              key={item.documentReleaseId}
              className="flex items-start gap-3 border-b pb-3"
            >
              <div className="flex w-1/3 items-center gap-1">
                <span className="line-clamp-2 text-sm font-medium">
                  {item.title}
                </span>
                <Button variant="ghost" size="icon-sm" asChild>
                  <a
                    href={`/documents/${item.documentId}?releaseId=${item.documentReleaseId}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t(($) => $.anchoring.quiz.viewDocument)}
                  >
                    <ExternalLink />
                  </a>
                </Button>
              </div>
              <Textarea
                value={item.prompt}
                onChange={(event) =>
                  setPrompt(item.documentReleaseId, event.target.value)
                }
                placeholder={t(($) => $.anchoring.create.promptPlaceholder)}
                aria-label={t(($) => $.anchoring.create.promptPlaceholder)}
                disabled={isCreating}
                className="min-h-10 flex-1"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t(($) => $.anchoring.create.removeDocument)}
                onClick={() => removeItem(item.documentReleaseId)}
                disabled={isCreating}
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button onClick={create} disabled={isCreating || items.length === 0}>
            {isCreating && (
              <Loader2 data-icon="inline-start" className="animate-spin" />
            )}
            {t(($) => $.anchoring.create.create)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateQuizzesDialog;
